// src/shared/components/print/PrintableTasks.jsx
// ✅ تاريخ موحد DD/MM/YYYY
import React, { forwardRef } from 'react';
import styles from './PrintableReport.module.css';
import { formatDate, formatDateTime } from '../../utils/formatters';

const PrintableTasks = forwardRef(({ tasks, filters, settings, users }, ref) => {
  if (!tasks || !settings || !users) return null;

  const getAssigneeFullName = (assigned_to_id) => {
    const user = users.find(u => u.id === assigned_to_id);
    if (!user) return 'غير مخصص';
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return fullName || user.username;
  };

  const getStatusName = (s) => ({
    todo: 'مطلوبة', in_progress: 'قيد التنفيذ', done: 'مكتملة', cancelled: 'ملغاة',
  }[s] || s);

  const getPriorityName = (p) => ({
    high: 'عالية', medium: 'متوسطة', low: 'منخفضة',
  }[p] || p);

  const getFilterValue = (key, value) => {
    if (!value || value === 'all') return 'الكل';
    if (key === 'filterByStatus') return value === 'active' ? 'المهام النشطة' : getStatusName(value);
    if (key === 'filterByUser') {
      const user = users.find(u => u.id === parseInt(value, 10));
      return user ? getAssigneeFullName(user.id) : value;
    }
    return value;
  };

  return (
    <div ref={ref} className={styles.printContainer}>

      <header className={styles.printHeader}>
        {settings.banner && (
          <img src={settings.banner} alt="Company Banner" className={styles.bannerImage} />
        )}
      </header>

      <main className={styles.printContent}>
        <h1 className={styles.mainTitle}>تقرير المهام</h1>

        <div className={styles.filterInfo}>
          <span><strong>من:</strong> {filters.start_date ? formatDate(filters.start_date) : 'البداية'}</span>
          <span><strong>إلى:</strong> {filters.end_date ? formatDate(filters.end_date) : 'النهاية'}</span>
          <span><strong>الحالة:</strong> {getFilterValue('filterByStatus', filters.filterByStatus)}</span>
          {filters.filterByUser && (
            <span><strong>المستخدم:</strong> {getFilterValue('filterByUser', filters.filterByUser)}</span>
          )}
          <span style={{ gridColumn: '1 / -1' }}>
            <strong>تاريخ التقرير:</strong> {formatDate(new Date())}
          </span>
        </div>

        <section className={styles.tableSection}>
          <h2 className={styles.sectionTitle}>قائمة المهام ({tasks.length} مهمة)</h2>
          {tasks.length > 0 ? (
            <div className={styles.tableContainer}>
              <table className={styles.reportTable}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th style={{ width: '25%' }}>عنوان المهمة</th>
                    <th>الحالة</th>
                    <th>الأولوية</th>
                    <th>المسؤول</th>
                    <th>تاريخ الإنشاء</th>
                    <th>تاريخ الاستحقاق</th>
                    <th>تاريخ الإكمال</th>
                    <th style={{ width: '15%' }}>سبب الإلغاء</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, index) => (
                    <tr key={task.id}>
                      <td style={{ textAlign: 'center' }}>{index + 1}</td>
                      <td>{task.title}</td>
                      <td style={{ textAlign: 'center' }}>{getStatusName(task.status)}</td>
                      <td style={{ textAlign: 'center' }}>{getPriorityName(task.priority)}</td>
                      <td style={{ textAlign: 'center' }}>{getAssigneeFullName(task.assigned_to?.id)}</td>
                      <td style={{ textAlign: 'center' }}>{formatDateTime(task.created_at)}</td>
                      <td style={{ textAlign: 'center' }}>{formatDate(task.due_date)}</td>
                      <td style={{ textAlign: 'center' }}>{formatDateTime(task.completed_at)}</td>
                      <td>{task.cancellation_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ textAlign: 'center', padding: '20px' }}>
              لا توجد مهام تطابق الفلاتر المحددة.
            </p>
          )}
        </section>
      </main>

      <footer className={styles.printFooter}>
        <div className={styles.footerContent}>
          {settings.company_name && <p>{settings.company_name}</p>}
          {settings.phone_number && <p>{settings.phone_number}</p>}
          {settings.address      && <p>{settings.address}</p>}
        </div>
      </footer>

    </div>
  );
});

PrintableTasks.displayName = 'PrintableTasks';
export default PrintableTasks;